import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { API_WEB_SOCKET } from './util/api.endpoints';
import { WebSocketService } from './websocket.service';
import { JobService } from './job/job.service';

export interface OutreachActivityEvent {
  type: string;
  logLine: string;
  level: string;
  channel: string;
  activityType: string;
  message: string;
  metadata?: any;
  seq?: number;
  displayTime: string;
  displayChannel: string;
  actionLabel: string;
  metaRight: string;
}

export type StreamChannelFilter = 'ALL' | 'CALL' | 'WA' | 'EMAIL' | 'TASKS';

@Injectable({ providedIn: 'root' })
export class OutreachActivityService implements OnDestroy {
  private wsSub: Subscription | null = null;
  private pollTimer: any = null;
  private pollGraceTimer: any = null;
  private activeProjectId: string | null = null;
  private activeEntityId: string | null = null;
  private seenSeq = new Set<number>();
  private readonly maxEvents = 300;
  private readonly pollIntervalMs = 15000;
  private readonly pollGraceMs = 5000;

  private eventsSubject = new BehaviorSubject<OutreachActivityEvent[]>([]);
  events$ = this.eventsSubject.asObservable();

  private connectedSubject = new BehaviorSubject<boolean>(false);
  connected$ = this.connectedSubject.asObservable();

  constructor(
    private wsService: WebSocketService,
    private jobService: JobService,
  ) { }

  ngOnDestroy() {
    this.teardown();
  }

  connect(projectId: string, entityId: string) {
    if (!projectId || !entityId) {
      return;
    }

    if (this.activeProjectId && this.activeProjectId !== projectId) {
      this.unsubscribe(this.activeProjectId);
    }

    this.activeProjectId = projectId;
    this.activeEntityId = entityId;
    this.seenSeq.clear();
    this.eventsSubject.next([]);
    this.connectedSubject.next(false);
    this.stopPolling();

    this.wsService.ensureConnected(API_WEB_SOCKET);
    this.wsSub = this.wsService.messages$.subscribe((msg) => this.handleWsMessage(msg));

    this.wsService.send({
      action: 'subscribeOutreachActivity',
      data: { projectId, entityId }
    });

    this.loadBuffer(projectId);

    if (this.wsService.isOpen()) {
      this.onWsLive();
    } else {
      this.scheduleFallbackPolling(projectId);
    }
  }

  unsubscribe(projectId: string) {
    if (!projectId) {
      return;
    }
    this.wsService.send({
      action: 'unsubscribeOutreachActivity',
      data: { projectId }
    });
  }

  teardown() {
    if (this.activeProjectId) {
      this.unsubscribe(this.activeProjectId);
    }
    if (this.wsSub) {
      this.wsSub.unsubscribe();
      this.wsSub = null;
    }
    this.stopPolling();
    this.activeProjectId = null;
    this.activeEntityId = null;
    this.connectedSubject.next(false);
  }

  loadBuffer(projectId: string, limit = 50) {
    this.jobService.getOutreachActivityBuffer(projectId, limit).subscribe(
      (res: any) => {
        const items = this.extractEventsFromResponse(res);
        items.forEach((item) => this.pushEvent(item, false));
        this.eventsSubject.next([...this.getCurrentEvents()]);
      },
      () => { /* polling/initial buffer may 404 until backend ships */ }
    );
  }

  private scheduleFallbackPolling(projectId: string) {
    if (this.pollGraceTimer) {
      clearTimeout(this.pollGraceTimer);
      this.pollGraceTimer = null;
    }
    this.pollGraceTimer = setTimeout(() => {
      this.pollGraceTimer = null;
      if (this.activeProjectId === projectId && !this.connectedSubject.getValue()) {
        this.startPolling(projectId);
      }
    }, this.pollGraceMs);
  }

  private startPolling(projectId: string) {
    if (this.pollTimer || this.connectedSubject.getValue()) {
      return;
    }
    this.pollTimer = setInterval(() => {
      if (this.activeProjectId === projectId && !this.connectedSubject.getValue()) {
        this.loadBuffer(projectId);
      }
    }, this.pollIntervalMs);
  }

  private stopPolling() {
    if (this.pollGraceTimer) {
      clearTimeout(this.pollGraceTimer);
      this.pollGraceTimer = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private onWsLive() {
    this.connectedSubject.next(true);
    this.stopPolling();
  }

  private handleWsMessage(msg: any) {
    if (msg === 'CONNECTED') {
      this.onWsLive();
      if (this.activeProjectId && this.activeEntityId) {
        this.wsService.send({
          action: 'subscribeOutreachActivity',
          data: { projectId: this.activeProjectId, entityId: this.activeEntityId }
        });
      }
      return;
    }

    if (msg === 'DISCONNECTED') {
      this.connectedSubject.next(false);
      if (this.activeProjectId) {
        this.scheduleFallbackPolling(this.activeProjectId);
      }
      return;
    }

    if (!msg || msg.type !== 'outreach_activity') {
      return;
    }

    this.onWsLive();
    this.pushEvent(msg, true);
    this.eventsSubject.next([...this.getCurrentEvents()]);
  }

  private pushEvent(raw: any, fromLive: boolean) {
    if (raw.seq != null && this.seenSeq.has(raw.seq)) {
      return;
    }
    if (raw.seq != null) {
      this.seenSeq.add(raw.seq);
    }

    const parsed = this.normalizeEvent(raw);
    const current = this.getCurrentEvents();
    current.push(parsed);

    if (current.length > this.maxEvents) {
      current.splice(0, current.length - this.maxEvents);
    }

    if (fromLive) {
      this.onWsLive();
    }
  }

  private getCurrentEvents(): OutreachActivityEvent[] {
    return this.eventsSubject.getValue();
  }

  private extractEventsFromResponse(res: any): any[] {
    if (!res) {
      return [];
    }
    if (Array.isArray(res)) {
      return res;
    }
    if (Array.isArray(res.events)) {
      return res.events;
    }
    if (Array.isArray(res.data)) {
      return res.data;
    }
    if (Array.isArray(res.items)) {
      return res.items;
    }
    return [];
  }

  normalizeEvent(raw: any): OutreachActivityEvent {
    const channel = (raw.channel || '').toLowerCase();
    const displayChannel = this.mapChannelLabel(channel);
    const actionLabel = this.mapActionLabel(raw.activityType, raw.message);
    const metaRight = this.buildMetaRight(raw);

    return {
      type: raw.type || 'outreach_activity',
      logLine: raw.logLine || '',
      level: raw.level || 'INFO',
      channel,
      activityType: raw.activityType || '',
      message: raw.message || raw.logLine || '',
      metadata: raw.metadata,
      seq: raw.seq,
      displayTime: this.extractDisplayTime(raw.logLine),
      displayChannel,
      actionLabel,
      metaRight,
    };
  }

  mapChannelLabel(channel: string): string {
    if (channel === 'voice' || channel === 'call') {
      return 'CALL';
    }
    if (channel === 'whatsapp' || channel === 'wa') {
      return 'WA';
    }
    if (channel === 'email') {
      return 'EMAIL';
    }
    if (channel === 'tasks' || channel === 'task') {
      return 'TASKS';
    }
    return (channel || 'LOG').toUpperCase();
  }

  matchesChannelFilter(event: OutreachActivityEvent, filter: StreamChannelFilter): boolean {
    if (filter === 'ALL') {
      return true;
    }
    return event.displayChannel === filter;
  }

  private extractDisplayTime(logLine: string): string {
    if (!logLine) {
      const now = new Date();
      return `${this.pad(now.getHours())}:${this.pad(now.getMinutes())}:${this.pad(now.getSeconds())}`;
    }
    const isoMatch = logLine.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2})/);
    if (isoMatch) {
      const d = new Date(isoMatch[1] + 'Z');
      return `${this.pad(d.getHours())}:${this.pad(d.getMinutes())}:${this.pad(d.getSeconds())}`;
    }
    const timeMatch = logLine.match(/(\d{2}:\d{2}:\d{2})/);
    return timeMatch ? timeMatch[1] : '--:--:--';
  }

  private mapActionLabel(activityType: string, message: string): string {
    const type = (activityType || '').toLowerCase();
    if (type.includes('reply')) return 'replied';
    if (type.includes('complete')) return 'completed';
    if (type.includes('connect')) return 'connected';
    if (type.includes('spawn') || type.includes('create')) return 'spawned';
    if (type.includes('send') || type.includes('sent')) return 'sent';
    if (type.includes('dial') || type.includes('call_init')) return 'dialing';
    if (type.includes('init')) return 'started';
    const msg = (message || '').toLowerCase();
    if (msg.includes('reply')) return 'replied';
    if (msg.includes('call')) return 'calling';
    return 'event';
  }

  private buildMetaRight(raw: any): string {
    const meta = raw.metadata || {};
    if (meta.task_id) {
      return `T-${meta.task_id}`;
    }
    if (meta.duration) {
      return `${meta.duration} duration`;
    }
    if (meta.score != null) {
      return `score ${meta.score}`;
    }
    if (meta.progress && meta.progress.completed != null && meta.progress.required != null) {
      return `${meta.progress.completed}/${meta.progress.required}`;
    }
    if (raw.activityType) {
      return String(raw.activityType).replace(/_/g, ' ');
    }
    return '';
  }

  private pad(n: number): string {
    return n < 10 ? `0${n}` : `${n}`;
  }
}
